import { RecordingCreationType, Subject } from "@/models/recording";
import { removeNewRecordingData } from "@/storage/recording";
import { AudioMimeType } from "@/types";
import { hashBlob } from "@/utils/fileHash";

export interface ChunkData {
  id: string;
  recordingId: string;
  chunkBlob: Blob;
  blockId: string;
  timestamp: number;
  uploaded: boolean;
}

export interface FinalBlockList {
  id: string;
  recordingId: string;
  blockListXML: string;
  timestamp: number;
  uploaded: boolean;
}

export type RecordingData = {
  id: string;
  recordingUUID: string;
  audioInput: string;
  subjects: Subject[];
  uploadURL: string;
  selectedLanguage: string;
  guestsList: string[];
  guestsListFullName: string[];
  guestsListInitials: string[];
  meetingId: string;
  meetingName: string;
  meetingDateTime: string;
  meetingStartDate: string;
  meetingEndDate: string;
  duration: string;
  durationTimestamp: number;
  status: number;
  data?: string;
  timestamp: number;
  subjectIndex: number;
  audioHash?: string;
  isSync?: boolean;
  recordingType?: RecordingCreationType;
};

const DB_NAME = "AudioRecorderDB";
const DB_VERSION = 2;
const CHUNKS_STORE = "chunks";
const BLOCKLIST_STORE = "blocklists";
const RECORDING_DATA_STORE = "recordingData";

let db: IDBDatabase | null = null;

export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("IndexedDB error:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      // IndexedDB initialized successfully
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Create chunks object store
      if (!database.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunksStore = database.createObjectStore(CHUNKS_STORE, { keyPath: "id" });
        chunksStore.createIndex("recordingId", "recordingId", { unique: false });
        chunksStore.createIndex("uploaded", "uploaded", { unique: false });
      }

      // Create blocklists object store
      if (!database.objectStoreNames.contains(BLOCKLIST_STORE)) {
        const blocklistStore = database.createObjectStore(BLOCKLIST_STORE, { keyPath: "id" });
        blocklistStore.createIndex("recordingId", "recordingId", { unique: false });
        blocklistStore.createIndex("uploaded", "uploaded", { unique: false });
      }

      // Create recording data object store
      if (!database.objectStoreNames.contains(RECORDING_DATA_STORE)) {
        const recordingDataStore = database.createObjectStore(RECORDING_DATA_STORE, { keyPath: "id" });
        recordingDataStore.createIndex("recordingUUID", "recordingUUID", { unique: false });
        recordingDataStore.createIndex("timestamp", "timestamp", { unique: false });
      }
    };
  });
};

export const saveRecordingDataToDB = async (localStorageData: string): Promise<void> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    try {
      const parsedData = JSON.parse(localStorageData);
      const recordingUUID = parsedData.recordingUUID;

      const recordingData: RecordingData = {
        id: `recording-${recordingUUID}`,
        recordingUUID: recordingUUID,
        uploadURL: parsedData.uploadURL,
        data: localStorageData,
        timestamp: Date.now(),
        ...parsedData,
      };

      const transaction = database.transaction([RECORDING_DATA_STORE], "readwrite");
      const store = transaction.objectStore(RECORDING_DATA_STORE);
      const request = store.put(recordingData);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("Error saving recording data to IndexedDB:", request.error);
        reject(request.error);
      };
    } catch (error) {
      console.error("Error parsing localStorage data:", error);
      reject(error);
    }
  });
};

const getTimestampFromValueField = (record: RecordingData): number => {
  try {
    const parsed = JSON.parse(record.data || "{}");
    const valueField = parsed?.value;

    if (valueField && typeof valueField === "object" && valueField.timestamp != null) {
      const timestamp = Number(valueField.timestamp);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }

    if (valueField && typeof valueField === "string") {
      const parsedValue = JSON.parse(valueField);
      const timestamp = Number(parsedValue?.timestamp);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }
  } catch {
    console.error("Error extracting timestamp from recording data, defaulting to record timestamp");
  }

  return record.timestamp;
};

export const getRecordingDataFromDB = async (recordingUUID: string): Promise<RecordingData | null> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([RECORDING_DATA_STORE], "readwrite");
    const store = transaction.objectStore(RECORDING_DATA_STORE);
    const index = store.index("recordingUUID");
    const request = index.getAll(recordingUUID);

    let latestRecord: RecordingData | null = null;

    request.onsuccess = () => {
      const records = request.result as RecordingData[];

      if (!records || records.length === 0) {
        latestRecord = null;
        return;
      }

      const sortedRecords = [...records].sort((a, b) => getTimestampFromValueField(b) - getTimestampFromValueField(a));

      latestRecord = sortedRecords[0];

      sortedRecords.slice(1).forEach((record) => {
        store.delete(record.id);
      });
    };

    request.onerror = () => {
      console.error("Error getting recording data from IndexedDB:", request.error);
      reject(request.error);
    };

    transaction.oncomplete = () => {
      resolve(latestRecord);
    };

    transaction.onerror = () => {
      console.error("Error deduplicating recording data in IndexedDB:", transaction.error);
      reject(transaction.error);
    };

    transaction.onabort = () => {
      console.error("Transaction aborted while deduplicating recording data:", transaction.error);
      reject(transaction.error);
    };
  });
};

export const saveChunkToDB = async (chunkData: ChunkData): Promise<void> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CHUNKS_STORE], "readwrite");
    const store = transaction.objectStore(CHUNKS_STORE);
    const request = store.add(chunkData);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error("Error saving chunk to IndexedDB:", request.error);
      reject(request.error);
    };
  });
};

export const saveBlockListToDB = async (blockListData: FinalBlockList): Promise<void> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([BLOCKLIST_STORE], "readwrite");
    const store = transaction.objectStore(BLOCKLIST_STORE);
    const request = store.add(blockListData);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error("Error saving BlockList to IndexedDB:", request.error);
      reject(request.error);
    };
  });
};

export const markChunkAsUploaded = async (chunkId: string): Promise<void> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CHUNKS_STORE], "readwrite");
    const store = transaction.objectStore(CHUNKS_STORE);
    const getRequest = store.get(chunkId);

    getRequest.onsuccess = () => {
      const chunk = getRequest.result;
      if (chunk) {
        chunk.uploaded = true;
        const updateRequest = store.put(chunk);

        updateRequest.onsuccess = () => {
          resolve();
        };

        updateRequest.onerror = () => {
          reject(updateRequest.error);
        };
      } else {
        reject(new Error("Chunk not found"));
      }
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
};

export const markAllChunksAsNotUploaded = async (recordingId: string): Promise<number> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CHUNKS_STORE], "readwrite");
    const store = transaction.objectStore(CHUNKS_STORE);
    const index = store.index("recordingId");
    const request = index.getAll(recordingId);

    request.onsuccess = () => {
      const chunks = request.result as ChunkData[];
      if (!chunks.length) {
        resolve(0);
        return;
      }

      let completed = 0;
      const updateErrors: unknown[] = [];

      chunks.forEach((chunk) => {
        const updatedChunk: ChunkData = {
          ...chunk,
          uploaded: false,
        };

        const updateRequest = store.put(updatedChunk);
        updateRequest.onsuccess = () => {
          completed += 1;
          if (completed === chunks.length) {
            resolve(chunks.length);
          }
        };

        updateRequest.onerror = () => {
          updateErrors.push(updateRequest.error);
          if (updateErrors.length === 1) {
            reject(updateRequest.error);
          }
        };
      });
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

export const markBlockListAsUploaded = async (blockListId: string): Promise<void> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([BLOCKLIST_STORE], "readwrite");
    const store = transaction.objectStore(BLOCKLIST_STORE);
    const getRequest = store.get(blockListId);

    getRequest.onsuccess = () => {
      const blockList = getRequest.result;
      if (blockList) {
        blockList.uploaded = true;
        const updateRequest = store.put(blockList);

        updateRequest.onsuccess = () => {
          resolve();
        };

        updateRequest.onerror = () => {
          reject(updateRequest.error);
        };
      } else {
        reject(new Error("BlockList not found"));
      }
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
};

export const clearRecordingData = async (recordingId: string): Promise<void> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CHUNKS_STORE, BLOCKLIST_STORE, RECORDING_DATA_STORE], "readwrite");
    const chunksStore = transaction.objectStore(CHUNKS_STORE);
    const blocklistStore = transaction.objectStore(BLOCKLIST_STORE);
    const recordingDataStore = transaction.objectStore(RECORDING_DATA_STORE);

    const chunksIndex = chunksStore.index("recordingId");
    const blocklistIndex = blocklistStore.index("recordingId");
    const recordingDataIndex = recordingDataStore.index("recordingUUID");

    const deleteChunks = chunksIndex.getAllKeys(recordingId);
    const deleteBlocklists = blocklistIndex.getAllKeys(recordingId);
    const deleteRecordingData = recordingDataIndex.getAllKeys(recordingId);

    Promise.all([
      // Delete chunks
      new Promise((res) => {
        deleteChunks.onsuccess = () => {
          const deletePromises = deleteChunks.result.map(
            (key) =>
              new Promise((deleteRes) => {
                const deleteReq = chunksStore.delete(key);
                deleteReq.onsuccess = () => deleteRes(undefined);
              }),
          );
          Promise.all(deletePromises).then(res);
        };
      }),
      // Delete blocklists
      new Promise((res) => {
        deleteBlocklists.onsuccess = () => {
          const deletePromises = deleteBlocklists.result.map(
            (key) =>
              new Promise((deleteRes) => {
                const deleteReq = blocklistStore.delete(key);
                deleteReq.onsuccess = () => deleteRes(undefined);
              }),
          );
          Promise.all(deletePromises).then(res);
        };
      }),
      // Delete recording data
      new Promise((res) => {
        deleteRecordingData.onsuccess = () => {
          const deletePromises = deleteRecordingData.result.map(
            (key) =>
              new Promise((deleteRes) => {
                const deleteReq = recordingDataStore.delete(key);
                deleteReq.onsuccess = () => deleteRes(undefined);
              }),
          );
          Promise.all(deletePromises).then(res);
        };
      }),
    ])
      .then(() => {
        resolve();
      })
      .catch(reject);
  });
};

// Method to find blocklist by recordingUUID pattern
export const findBlockListByRecordingId = async (recordingId: string): Promise<FinalBlockList | null> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([BLOCKLIST_STORE], "readonly");
    const store = transaction.objectStore(BLOCKLIST_STORE);
    const request = store.index("recordingId").getAll(recordingId);

    request.onsuccess = () => {
      const blockLists = request.result as FinalBlockList[];
      const matchingBlockList = [...blockLists].sort((a, b) => b.timestamp - a.timestamp)[0] ?? null;

      resolve(matchingBlockList);
    };

    request.onerror = () => {
      console.error("Error getting blocklists from IndexedDB:", request.error);
      reject(request.error);
    };
  });
};

// Method to find all chunks by recordingUUID pattern
export const findChunksByRecordingId = async (recordingId: string): Promise<ChunkData[]> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CHUNKS_STORE], "readonly");
    const store = transaction.objectStore(CHUNKS_STORE);
    const request = store.index("recordingId").getAll(recordingId);

    request.onsuccess = () => {
      const chunks = request.result as ChunkData[];
      const matchingChunks = [...chunks].sort((a, b) => a.timestamp - b.timestamp);

      resolve(matchingChunks);
    };

    request.onerror = () => {
      console.error("Error getting chunks from IndexedDB:", request.error);
      reject(request.error);
    };
  });
};

// Helper function to combine audio chunks
const combineAudioChunks = async (chunks: ChunkData[], mimeType: string = AudioMimeType.Webm): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      // Extract blobs from chunks
      const blobArray: Blob[] = chunks.map((chunk) => chunk.chunkBlob);

      // Combine all blobs into one
      const combinedBlob = new Blob(blobArray, { type: mimeType });

      // Combined chunks into single blob
      resolve(combinedBlob);
    } catch (error) {
      console.error("Error combining blobs:", error);
      reject(error);
    }
  });
};

// Reconstruct audio chunks and compute a SHA-256 hash of the resulting file
export const getAudioHashFromRecordingId = async (recordingId: string): Promise<string> => {
  const audioBlob = await reconstructAudioFromRecordingId(recordingId);

  return hashBlob(audioBlob);
};

export const reconstructAudioFromRecordingId = async (recordingId: string) => {
  try {
    const chunks = await findChunksByRecordingId(recordingId);

    if (!chunks || chunks.length === 0) {
      console.warn("No chunks found for recording:", recordingId);
      throw new Error(`Error: No chunks found for recording:${recordingId}`);
    }

    // Step 3: Sort chunks by timestamp to maintain order
    const sortedChunks = chunks.sort((a, b) => a.timestamp - b.timestamp);

    const recordingData = await getRecordingDataFromDB(recordingId);
    let resolvedMimeType = sortedChunks[0]?.chunkBlob?.type || AudioMimeType.Webm;

    if (recordingData?.data) {
      const parsedData = JSON.parse(recordingData.data);
      if (typeof parsedData?.uploadFileType === "string" && parsedData.uploadFileType.length > 0) {
        resolvedMimeType = parsedData.uploadFileType;
      }
    }
    // Step 4: Combine all chunk blobs into one audio file
    const combinedBlob = await combineAudioChunks(sortedChunks, resolvedMimeType);

    // Successfully reconstructed audio file
    return combinedBlob;
  } catch (error) {
    console.error("Error in reconstructAudioFromRecordingId:", error);
    throw error;
  }
};

// Updated method to download the reconstructed audio file using recording ID
export const downloadReconstructedAudioById = async (recordingId: string, fileName?: string): Promise<void> => {
  try {
    const audioBlob = await reconstructAudioFromRecordingId(recordingId);

    if (!audioBlob) {
      throw new Error("No audio data found for the given recording ID");
    }

    const mimeType = audioBlob.type || AudioMimeType.Webm;
    const extensionFromMime = mimeType.includes("/") ? mimeType.split("/")[1] : "webm";
    const normalizedExtension = extensionFromMime.split(";")[0] || "webm";

    // Create download link
    const url = URL.createObjectURL(audioBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName ? `${fileName}.${normalizedExtension}` : `recording-${recordingId}.${normalizedExtension}`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading reconstructed audio:", error);
    throw error;
  }
};

// Method to get all unique recordingUUIDs from recordingData table
export const getAllRecordingUUIDs = async (): Promise<string[]> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([RECORDING_DATA_STORE], "readonly");
    const store = transaction.objectStore(RECORDING_DATA_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const recordingDataList = request.result;

      // Extract recording UUIDs, filter out null/undefined/empty values, and remove duplicates
      const recordingUUIDs = recordingDataList
        .map((recordingData) => recordingData.recordingUUID)
        .filter((uuid): uuid is string => {
          // Filter out null, undefined, empty strings, and whitespace-only strings
          return uuid != null && typeof uuid === "string" && uuid.trim().length > 0;
        })
        .filter((uuid, index, array) => array.indexOf(uuid) === index); // Remove duplicates

      resolve(recordingUUIDs);
    };

    request.onerror = () => {
      console.error("Error getting recording UUIDs from IndexedDB:", request.error);
      reject(request.error);
    };
  });
};

// Method to check if all chunks and blocklist are uploaded and clear data if complete
export const checkAndClearCompletedRecording = async (
  recordingId: string,
): Promise<{
  allUploaded: boolean;
  cleared: boolean;
  summary: {
    totalChunks: number;
    uploadedChunks: number;
    blocklistUploaded: boolean;
    missingData: string[];
  };
}> => {
  try {
    // Step 1: Find all chunks for this recording
    const chunks = await findChunksByRecordingId(recordingId);

    // Step 2: Find the blocklist for this recording
    const blockList = await findBlockListByRecordingId(recordingId);

    // Step 3: Check if recording data exists
    const recordingData = await getRecordingDataFromDB(recordingId);

    // Initialize summary
    const summary = {
      totalChunks: chunks.length,
      uploadedChunks: 0,
      blocklistUploaded: false,
      missingData: [] as string[],
    };

    // Check if we have any data at all
    if (chunks.length === 0 && !blockList && !recordingData) {
      console.warn("No data found for recording:", recordingId);
      return {
        allUploaded: false,
        cleared: false,
        summary,
      };
    }

    // if no chunks local it should clean the local data
    if (!chunks.length) {
      await clearRecordingData(recordingId);
      removeNewRecordingData(recordingId);
    }

    // Count uploaded chunks
    summary.uploadedChunks = chunks.filter((chunk) => chunk.uploaded).length;

    // // Check if blocklist is uploaded
    // summary.blocklistUploaded = blockList ? blockList.uploaded : false;

    // Identify what's missing
    if (summary.uploadedChunks < summary.totalChunks) {
      summary.missingData.push(`${summary.totalChunks - summary.uploadedChunks} chunks not uploaded`);
    }

    if (blockList && !blockList.uploaded) {
      summary.missingData.push("blocklist not uploaded");
    }

    // if (!blockList && chunks.length > 0) {
    //   summary.missingData.push('blocklist missing');
    // }

    // Determine if everything is uploaded
    const allChunksUploaded = summary.uploadedChunks === summary.totalChunks;
    // const blocklistComplete = blockList ? blockList.uploaded : (chunks.length === 0);
    // const allUploaded = allChunksUploaded && blocklistComplete;

    // Step 4: Clear data if everything is uploaded
    // if (allUploaded) {
    if (allChunksUploaded) {
      await clearRecordingData(recordingId);
      removeNewRecordingData(recordingId);

      return {
        allUploaded: true,
        cleared: true,
        summary,
      };
    } else {
      return {
        allUploaded: false,
        cleared: false,
        summary,
      };
    }
  } catch (error) {
    console.error("Error checking and clearing completed recording:", error);
    throw error;
  }
};

// Helper method to check upload status without clearing (for monitoring)
export const getUploadStatus = async (
  recordingId: string,
): Promise<{
  recordingId: string;
  exists: boolean;
  totalChunks: number;
  uploadedChunks: number;
  pendingChunks: number;
  blocklistExists: boolean;
  blocklistUploaded: boolean;
  recordingDataExists: boolean;
  uploadProgress: number; // Percentage 0-100
}> => {
  try {
    // Find all chunks for this recording
    const chunks = await findChunksByRecordingId(recordingId);

    // Find the blocklist for this recording
    const blockList = await findBlockListByRecordingId(recordingId);

    // Check if recording data exists
    const recordingData = await getRecordingDataFromDB(recordingId);

    const totalChunks = chunks.length;
    const uploadedChunks = chunks.filter((chunk) => chunk.uploaded).length;
    const pendingChunks = totalChunks - uploadedChunks;
    const blocklistExists = blockList !== null;
    const blocklistUploaded = blockList ? blockList.uploaded : false;
    const recordingDataExists = recordingData !== null;

    // Calculate overall progress (chunks + blocklist)
    const totalItems = totalChunks + (blocklistExists ? 1 : 0);
    const uploadedItems = uploadedChunks + (blocklistUploaded ? 1 : 0);
    const uploadProgress = totalItems > 0 ? Math.round((uploadedItems / totalItems) * 100) : 0;

    return {
      recordingId,
      exists: totalChunks > 0 || blocklistExists || recordingDataExists,
      totalChunks,
      uploadedChunks,
      pendingChunks,
      blocklistExists,
      blocklistUploaded,
      recordingDataExists,
      uploadProgress,
    };
  } catch (error) {
    console.error("Error getting upload status:", error);
    throw error;
  }
};

export const resetUploadStatusIfIncomplete = async (
  recordingId: string,
): Promise<{
  hadIncompleteData: boolean;
  updatedChunks: number;
  updatedBlocklists: number;
}> => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CHUNKS_STORE, BLOCKLIST_STORE], "readwrite");
    const chunksStore = transaction.objectStore(CHUNKS_STORE);
    const blocklistStore = transaction.objectStore(BLOCKLIST_STORE);

    const chunksRequest = chunksStore.index("recordingId").getAll(recordingId);
    const blocklistsRequest = blocklistStore.index("recordingId").getAll(recordingId);

    let chunks: ChunkData[] = [];
    let blockLists: FinalBlockList[] = [];
    let pendingReads = 2;
    let updatedChunks = 0;
    let updatedBlocklists = 0;
    let result = {
      hadIncompleteData: false,
      updatedChunks: 0,
      updatedBlocklists: 0,
    };
    let isSettled = false;

    const rejectOnce = (error: unknown, message: string) => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      console.error(message, error);
      reject(error instanceof Error ? error : new Error(message));
    };

    const tryProcess = () => {
      pendingReads -= 1;
      if (pendingReads > 0) {
        return;
      }

      const hasIncompleteChunk = chunks.some((chunk) => !chunk.uploaded);
      const hasIncompleteBlockList = blockLists.some((blockList) => !blockList.uploaded);
      const hadIncompleteData = hasIncompleteChunk || hasIncompleteBlockList;

      if (!hadIncompleteData) {
        result = {
          hadIncompleteData: false,
          updatedChunks: 0,
          updatedBlocklists: 0,
        };
        return;
      }

      chunks
        .filter((chunk) => chunk.uploaded)
        .forEach((chunk) => {
          const updatedChunk: ChunkData = {
            ...chunk,
            uploaded: false,
          };
          chunksStore.put(updatedChunk);
          updatedChunks += 1;
        });

      blockLists
        .filter((blockList) => blockList.uploaded)
        .forEach((blockList) => {
          const updatedBlockList: FinalBlockList = {
            ...blockList,
            uploaded: false,
          };
          blocklistStore.put(updatedBlockList);
          updatedBlocklists += 1;
        });

      result = {
        hadIncompleteData: true,
        updatedChunks,
        updatedBlocklists,
      };
    };

    chunksRequest.onsuccess = () => {
      chunks = chunksRequest.result as ChunkData[];
      tryProcess();
    };

    blocklistsRequest.onsuccess = () => {
      blockLists = blocklistsRequest.result as FinalBlockList[];
      tryProcess();
    };

    chunksRequest.onerror = () => {
      rejectOnce(chunksRequest.error, "Error getting chunks by recordingId:");
      transaction.abort();
    };

    blocklistsRequest.onerror = () => {
      rejectOnce(blocklistsRequest.error, "Error getting blocklists by recordingId:");
      transaction.abort();
    };

    transaction.oncomplete = () => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      resolve(result);
    };

    transaction.onerror = () => {
      rejectOnce(transaction.error, "Error updating upload statuses:");
    };

    transaction.onabort = () => {
      rejectOnce(transaction.error, "Transaction aborted while resetting upload statuses:");
    };
  });
};
